import { signIn } from "next-auth/react";
import Image from "next/image";
import { usePathname } from "next/navigation";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
  useEffect,
} from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { toast } from "sonner";

import { Icons } from "@/components/shared/icons";
import { Button, buttonVariants } from "@/components/ui/button";
import { Modal } from "@/components/ui/modal";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EmailCodeForm } from "@/components/forms/email-code-form";
import { siteConfig } from "@/config/site";
import { userAuthSchema } from "@/lib/validations/auth";
import { cn } from "@/lib/utils";
import { trackClientEvent } from '@/lib/analyticsClient';

type FormData = z.infer<typeof userAuthSchema>;

function SignInModal({
  showSignInModal,
  setShowSignInModal,
  showFreeTrialMessage = false,
}: {
  showSignInModal: boolean;
  setShowSignInModal: Dispatch<SetStateAction<boolean>>;
  showFreeTrialMessage?: boolean;
}) {
  const [signInClicked, setSignInClicked] = useState(false);
  const [isEmailLoading, setIsEmailLoading] = useState(false);
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [pendingCallbackUrl, setPendingCallbackUrl] = useState("/dashboard");
  const pathname = usePathname();
  
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(userAuthSchema),
  });
  
  // Get referral code from URL params safely on client side
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      setReferralCode(urlParams.get("ref"));
    }
  }, []);

  useEffect(() => {
    if (!showSignInModal) {
      setPendingEmail(null);
      setSignInClicked(false);
    }
  }, [showSignInModal]);
  
  // Build registration_page param from pathname (replace / with -)
  const getRegistrationPageParam = () => {
    const page = (pathname || '/').replace(/^\//, '').replace(/\//g, '-') || 'home';
    return `registration_page=${encodeURIComponent(page)}`;
  };

  // Determine callback URL based on current page, search params, and trial intent
  const getCallbackUrl = () => {
    // Check search params first (for direct navigation) - client side only
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const fromParam = urlParams.get("from");
      const callbackParam = urlParams.get("callbackUrl");
      
      if (fromParam) {
        const sep = fromParam.includes('?') ? '&' : '?';
        return `${fromParam}${sep}${getRegistrationPageParam()}`;
      }
      if (callbackParam) {
        const sep = callbackParam.includes('?') ? '&' : '?';
        return `${callbackParam}${sep}${getRegistrationPageParam()}`;
      }
    }
    
    // Check if user has trial intent
    const trialIntent = typeof window !== 'undefined' ? sessionStorage.getItem('trialIntent') : null;
    
    if (trialIntent) {
      return `/auth/callback?intent=${trialIntent}&${getRegistrationPageParam()}`;
    }
    
    if (pathname === "/pricing" || pathname.startsWith("/pricing")) {
      return `/pricing?${getRegistrationPageParam()}`;
    }
    return `/dashboard?${getRegistrationPageParam()}`;
  };

  async function onEmailSubmit(data: FormData) {
    setIsEmailLoading(true);

    const email = data.email.toLowerCase();

    // Check for disposable email domains
    try {
      const validateRes = await fetch("/api/auth/validate-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      if (!validateRes.ok) {
        const { error } = await validateRes.json();
        setIsEmailLoading(false);
        return toast.error("Invalid email", { description: error });
      }
    } catch {
      // If validation endpoint fails, allow sign-in to proceed
    }

    const callbackUrl = getCallbackUrl();

    // Track email login attempt (fire-and-forget)
    trackClientEvent({ name: 'login_email_link_requested', params: { pathname } });

    const signInResult = await signIn("resend", {
      email,
      redirect: false,
      callbackUrl,
      ...(referralCode && { referralCode })
    });

    setIsEmailLoading(false);

    if (!signInResult?.ok) {
      return toast.error("Something went wrong.", {
        description: "Your sign in request failed. Please try again."
      });
    }

    setPendingEmail(email);
    setPendingCallbackUrl(callbackUrl);
  }

  const handleGoogleSignIn = () => {
    setSignInClicked(true);
    const callbackUrl = getCallbackUrl();
    
    // Track Google sign-in click (fire-and-forget)
    const trialIntent = typeof window !== 'undefined' ? sessionStorage.getItem('trialIntent') : null;
    trackClientEvent({ name: 'login_google_clicked', params: { trialIntent, pathname } });

    const signInOptions: any = { callbackUrl };

    if (referralCode) {
      signInOptions.referralCode = referralCode;
    }

    signIn("google", signInOptions);
  };

  return (
    <Modal showModal={showSignInModal} setShowModal={setShowSignInModal}>
      <div className="w-full">
        <div className="flex flex-col items-center justify-center space-y-3 border-b bg-background px-4 py-6 pt-8 text-center md:px-16">
          <a href={siteConfig.url}>
            <Image 
              src="/_static/landing/quantwheelicon-transparent.png" 
              alt={siteConfig.name}
              width={40} 
              height={40} 
              className="size-10"
            />
          </a>
          <h3 className="font-urban text-2xl font-bold">Sign In</h3>
          {showFreeTrialMessage ? (
            <p className="text-sm text-gray-500">
              Start free forever, or try any paid plan for just $1 for 7 days.
            </p>
          ) : (
            <p className="text-sm text-gray-500">
              We value your privacy, only your email and name will be stored. 
            </p>
          )}
          {referralCode && (
            <p className="text-xs text-center text-muted-foreground">
              You were referred by a friend
            </p>
          )}
        </div>

        <div className="flex flex-col space-y-4 bg-secondary/50 px-4 py-8 md:px-16">
          {pendingEmail ? (
            <EmailCodeForm
              email={pendingEmail}
              callbackUrl={pendingCallbackUrl}
              referralCode={referralCode}
              onBack={() => setPendingEmail(null)}
            />
          ) : (
            <>
              <Button
                variant="default"
                disabled={signInClicked || isEmailLoading}
                onClick={handleGoogleSignIn}
              >
                {signInClicked ? (
                  <Icons.spinner className="mr-2 size-4 animate-spin" />
                ) : (
                  <Icons.google className="mr-2 size-4" />
                )}{" "}
                Sign In with Google
              </Button>
              
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-secondary px-2 text-muted-foreground">
                    Or use email
                  </span>
                </div>
              </div>
              
              <form onSubmit={handleSubmit(onEmailSubmit)}>
                <div className="grid gap-2">
                  <div className="grid gap-1">
                    <Label className="sr-only" htmlFor="email">
                      Email
                    </Label>
                    <Input
                      id="email"
                      placeholder="name@example.com"
                      type="email"
                      autoCapitalize="none"
                      autoComplete="email"
                      autoCorrect="off"
                      disabled={isEmailLoading || signInClicked}
                      {...register("email")}
                    />
                    {errors?.email && (
                      <p className="px-1 text-xs text-red-600">
                        {errors.email.message}
                      </p>
                    )}
                  </div>
                  <button 
                    type="submit"
                    className={cn(buttonVariants({ variant: "outline" }))} 
                    disabled={isEmailLoading || signInClicked}
                  >
                    {isEmailLoading && (
                      <Icons.spinner className="mr-2 size-4 animate-spin" />
                    )}
                    Sign In with Email
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}

export function useSignInModal() {
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [showFreeTrialMessage, setShowFreeTrialMessage] = useState(false);

  const SignInModalCallback = useCallback(() => {
    return (
      <SignInModal
        showSignInModal={showSignInModal}
        setShowSignInModal={setShowSignInModal}
        showFreeTrialMessage={showFreeTrialMessage}
      />
    );
  }, [showSignInModal, setShowSignInModal, showFreeTrialMessage]);

  return useMemo(
    () => ({
      setShowSignInModal,
      setShowFreeTrialMessage,
      SignInModal: SignInModalCallback,
    }),
    [setShowSignInModal, setShowFreeTrialMessage, SignInModalCallback],
  );
}
